import * as React from "react";
import {useContext, useEffect} from "react";
import {ConversionJobLog, TransportOrder} from "../../../../../../generated/api";
import {backend} from "../../../../../../xconvert-backend";
import {authentication} from "../../../../../../authentication";
import {Button, Dropdown, Label} from "semantic-ui-react";
import {FileViewer} from "../../../../../util/fileViewer/FileViewer";
import {OpenFileRequest} from "../../../../../../generated-ftp/api";
import {ConfigContext} from "../../../../context/ConfigContext";
import {FileWithState, uploadedFiles} from "../../../../../util/FileUploadMultiple";
import {DownloadButton} from "../../../../../util/react-download-link/DownloadButton";

export interface TransportOrderImportFilesProps {
    order: TransportOrder | null;
    history: any;
}

export function TransportOrderImportFiles(props: React.PropsWithChildren<TransportOrderImportFilesProps>) {

    const context = useContext(ConfigContext)

    const [loading, setLoading] = React.useState<boolean>(true);
    const [jobLogs, setJobLogs] = React.useState<ConversionJobLog[]>([]);
    const [selectedJobLog, setSelectedJobLog] = React.useState<ConversionJobLog | null>(null);
    const [loadingFile, setLoadingFile] = React.useState<boolean>(false);
    const [loadedBlob, setLoadedBlob] = React.useState<Blob | null>(null);
    const [loadedFile, setLoadedFile] = React.useState<ArrayBuffer | null>(null);

    const [cachedFiles, setCachedFiles] = React.useState<{path: string, fileBuffer: Blob}[]>([]);

    useEffect(() => {
        fetchFiles().then()
    }, []);

    useEffect(() => {
        cleanCache()
        fetchFiles().then()
    }, [props.order]);

    function cleanCache() {
        setCachedFiles([]);
    }

    async function fetchFiles() {
        setLoading(true);

        try {
            let auth = backend.withTokenAuthHeader(authentication.token);
            let response = await backend.internalApi.fetchConversionJobLogForOrder(
                props.order!._id!,
                auth
            )
            setJobLogs(response.entries || []);
            setSelectedJobLog(response.entries[0])
            loadFile(response.entries[0].archiveFileName).catch(console.error);
        } finally {
            setLoading(false);
        }

    }

    if (props.order == null) {
        return <div>Please select a transportOrder</div>;
    }

    if (loading) {
        return <div>Loading files...</div>;
    }

    function openFileInImportSim(log: ConversionJobLog) {

        uploadedFiles.value = [{
            file: loadedBlob,
            fileName: log.archiveFileName.split("/").pop() || "unknown",
            state: "NOT_PROCESSED"
        } as FileWithState]

        // setting tab to importSim
        context.setSingleTabSession("converterTab", 1) // 1 = importSim
        context.setSingleTabSession("importSimTab", 1) // 1 = file

        // moving to convPage
        let url = "/" + context.companyId + "/convPage"
        props.history.push(url)
    }

    function renderJobLogs() {
        return <><Dropdown
            placeholder="Datei auswählen"
            selection
            options={jobLogs.map((log: ConversionJobLog) => ({
                key: log.inputFileName,
                value: log.inputFileName,
                text: log.inputFileName
            }))}
            value={selectedJobLog?.inputFileName || ""}
            onChange={(_, data) => {
                const selectedLog = jobLogs.find(log => log.inputFileName === data.value);
                if (selectedLog) {
                    setSelectedJobLog(selectedLog);
                    loadFile(selectedLog.archiveFileName).catch(console.error);
                }
            }}
        />
            <span style={{paddingLeft: 10}}>{jobLogs.length || 0} imported files</span>
        </>
    }

    function displayResult(result: ConversionJobLog.ResultEnum) {
        switch (result) {
            case ConversionJobLog.ResultEnum.SUCCESS:
                return <Label color={"green"}>SUCCESS</Label>;
            case ConversionJobLog.ResultEnum.PENDING:
                return <Label color={"yellow"}>WARNING</Label>;
            case ConversionJobLog.ResultEnum.FAILURE:
                return <Label color={"red"}>FAILED</Label>;
            default:
                return <Label>{result}</Label>;
        }
    }

    function renderFile() {
        if (!selectedJobLog) {
            return <></>;
        }

        if (loadingFile) {
            return <div>Loading file...</div>;
        }

        if(loadedFile == null) {
            return <div>No files found</div>;
        }

        return <>
            <div style={{padding: '0px'}}>
                <Button
                    id={selectedJobLog.inputFileName + "_importSimButton"}
                    onClick={() => {
                        openFileInImportSim(selectedJobLog);
                    }}>
                    open in ImportSim
                </Button>
                <span> result was {displayResult(selectedJobLog.result)}</span>
                {loadedFile && <div
                    style={{
                        float: "right",
                    }}>
                    <DownloadButton filename={selectedJobLog.inputFileName} exportFile={loadedFile}/>
                </div>}
            </div>

            <div style={{flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                <FileViewer
                    fileBuffer={loadedFile}
                    fileName={selectedJobLog.inputFileName || "unknown"}
                    displayName={false}
                />
            </div>
        </>

    }

    async function loadFile(path: string) {
        if(cachedFiles.some(f => f.path === path)) {
            let file = cachedFiles.find(f => f.path === path)
            setLoadedBlob(file?.fileBuffer || null)
            setLoadedFile(await file?.fileBuffer.arrayBuffer() || null)
            return;
        }
        setLoadingFile(true);

        try {
            let request = {} as OpenFileRequest
            request.fileName = path.split("/").pop();

            let auth = backend.withTokenAuthHeader(authentication.token);
            let pathWithoutFileName = path.split("/").slice(0, -1).join("/");


            let pathParameter = "?path=" + pathWithoutFileName + "&companyId=" + props.order.companyId ?? context.companyId


            let file = await fetch('/ftp/openFile' + pathParameter, {
                method: 'POST',
                headers: {
                    "Authorization": auth.headers.Authorization.toString(),
                    "Content-Type": "application/json; charset=utf-8"
                },
                body: JSON.stringify(request)
            })
            let blob = await file.blob()
            setLoadedBlob(blob)
            setLoadedFile(await blob.arrayBuffer())
            setCachedFiles(prev => [...prev, {path: path, fileBuffer: blob}])
        } finally {
            setLoadingFile(false);
        }

    }

    return <div style={{display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden'}}>
        <div>
            {renderJobLogs()}´
        </div>
        <div style={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            {renderFile()}
        </div>
    </div>;
}